import React from 'react';

import { Box, Link, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';

import { trimObjectValues } from '@hrlogics/helper-utils';
import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { SUPPORT_CLEARVERIFY_EMAIL } from '@clearverify/constants';

import { LOGIN_RESULT_DEFAULT_VALUE } from 'src/components/auth/Login/constants';
import type { LoginResultWithEmail } from 'src/components/auth/Login/types';
import { Button } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useLoginRedirectTarget } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';

import { LoginFormField, LoginFormValues } from './types';
import { loginValidationSchema } from './utils/validationSchemas';

type LoginMfaFormProps = {
  loginData: LoginResultWithEmail;
  handleLoginResult: (data: LoginResultWithEmail) => void;
};

export const LoginMfaForm: React.FC<LoginMfaFormProps> = ({ loginData, handleLoginResult }) => {
  const navigate = useNavigate();

  const { login } = useAppAuth();
  const { spawnNotification } = useNotification();
  const redirectTarget = useLoginRedirectTarget();

  // Catching a case if the user entered data into the form for too long.
  // Because session duration is only 5 minutes (configurable in Cognito)
  // The value is one minute less because we need to take network delays and render times into account.
  const sessionExpireTime = React.useRef(DateTime.now().plus({ minutes: 4 }));

  const submitHandler = React.useCallback(
    async (rawFormValues: LoginFormValues) => {
      if (DateTime.now() >= sessionExpireTime.current) {
        spawnNotification('The authorization session has expired. Log in again.', 'error');
        handleLoginResult({ ...LOGIN_RESULT_DEFAULT_VALUE, email: loginData.email });
        return;
      }

      try {
        const formValues = trimObjectValues(rawFormValues);

        const data = await login({
          email: loginData.email || '',
          session: loginData.session,
          userCode: formValues.verificationCode,
        });

        if (data.success) {
          navigate(redirectTarget, { replace: true });
        }
      } catch (error) {
        console.error(error);
        if (error instanceof Error) {
          spawnNotification(error.message, 'error');
        }
      }
    },
    [
      handleLoginResult,
      login,
      loginData.email,
      loginData.session,
      navigate,
      redirectTarget,
      spawnNotification,
    ],
  );

  return (
    <Form<LoginFormValues>
      onSubmit={submitHandler}
      subscription={{ valid: true, submitting: true }}
      containerProps={{ sx: formStyles }}
      validate={validateWithYupSchema(loginValidationSchema)}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Field
              name={LoginFormField.verificationCode}
              component={TextField}
              label="Verification Code"
              fullWidth
              required
              disabled={submitting}
              slotProps={{
                input: {
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                },
              }}
            />

            <Typography variant="body1" color="text.secondary" mt={-1}>
              Have troubles with access? Contact us:{' '}
              <Link href={`mailto:${SUPPORT_CLEARVERIFY_EMAIL}`}>{SUPPORT_CLEARVERIFY_EMAIL}</Link>
            </Typography>

            <Box textAlign="center" mt={2}>
              <Button
                type="submit"
                disabled={submitting || !valid}
                loading={submitting}
                size="large"
                variant="contained"
              >
                Sign In
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const formStyles = {
  display: 'grid',
  gap: 2,
} as const satisfies SxProp;
